(() => {
  const sectionSelector = "[data-scan-steps-section]";

  const getVideoMedia = (wrap) => wrap?.querySelector("video-media") ?? null;

  const pauseVideoMedia = (vm) => {
    if (!vm) return;
    vm.pause?.();
    const video = vm.querySelector("video");
    if (video) {
      video.pause();
    }
  };

  const playVideoMedia = (vm) => {
    if (!vm) return;
    vm.play?.();
  };

  const playStepVideo = (item, { fromStart = false } = {}) => {
    const { vm, video } = item;
    if (!video) return;

    if (fromStart) {
      try {
        video.currentTime = 0;
      } catch {
        /* ignore seek errors */
      }
    }

    playVideoMedia(vm);

    if (video.paused) {
      video.play().catch(() => {});
    }
  };

  const initStepVideos = (section) => {
    const isTimeline = section.getAttribute("data-scan-layout") === "timeline";

    section.querySelectorAll("[data-scan-step-media]").forEach((wrap) => {
      if (wrap.getAttribute("data-scan-video-bound") === "true") return;
      wrap.setAttribute("data-scan-video-bound", "true");

      const vm = getVideoMedia(wrap);
      if (!vm) return;

      if (isTimeline) return;

      if (wrap.hasAttribute("data-video-hover-play")) {
        wrap.addEventListener("mouseenter", () => playVideoMedia(vm));
        wrap.addEventListener("mouseleave", () => pauseVideoMedia(vm));
        wrap.addEventListener("focusin", () => playVideoMedia(vm));
        wrap.addEventListener("focusout", () => pauseVideoMedia(vm));
      }
    });
  };

  const syncTabPanelVideos = (section, activePanel) => {
    section.querySelectorAll("[data-scan-step-media]").forEach((wrap) => {
      const vm = getVideoMedia(wrap);
      if (!vm) return;

      const panel = wrap.closest("[data-step-panel]");
      const isActive = panel === activePanel;

      if (!isActive) {
        pauseVideoMedia(vm);
        return;
      }

      if (wrap.hasAttribute("data-video-autoplay")) {
        playVideoMedia(vm);
      }
    });
  };

  const initTabs = (section) => {
    const root = section.querySelector("[data-scan-steps-root]");
    if (!root) return;

    const tabs = Array.from(root.querySelectorAll("[data-step-tab]"));
    const panels = Array.from(root.querySelectorAll("[data-step-panel]"));
    if (!tabs.length || !panels.length) return;

    const activate = (id) => {
      let activePanel = null;

      tabs.forEach((tab) => {
        const isActive = tab.getAttribute("data-step-tab") === id;
        tab.classList.toggle("is-active", isActive);
        tab.setAttribute("aria-selected", isActive ? "true" : "false");
      });

      panels.forEach((panel) => {
        const isActive = panel.getAttribute("data-step-panel") === id;
        panel.classList.toggle("is-active", isActive);
        panel.hidden = !isActive;
        if (isActive) activePanel = panel;
      });

      syncTabPanelVideos(section, activePanel);
    };

    tabs.forEach((tab) => {
      tab.addEventListener("click", () => {
        activate(tab.getAttribute("data-step-tab"));
      });
    });

    const firstId = tabs[0]?.getAttribute("data-step-tab");
    if (firstId) activate(firstId);
  };

  const initTimelineSequence = (section, steps, signal) => {
    const timeline = section.querySelector("[data-youp-timeline]");
    if (!timeline?.hasAttribute("data-youp-timeline-sequence")) return null;

    const playable = steps
      .map((step) => {
        const wrap = step.querySelector("[data-scan-step-media]");
        const vm = getVideoMedia(wrap);
        const video = vm?.querySelector("video") ?? null;
        if (!wrap || !video) return null;
        return { step, wrap, vm, video };
      })
      .filter(Boolean);

    if (!playable.length) return null;

    let currentIndex = 0;
    let isRunning = false;
    let hoveredIndex = null;
    let endedHandler = null;
    let leaveTimer = null;
    let playToken = 0;

    const clearActive = () => {
      steps.forEach((step) => step.classList.remove("is-sequence-active"));
    };

    const setActive = (index) => {
      clearActive();
      playable[index]?.step.classList.add("is-sequence-active");
    };

    const pauseAll = () => {
      playable.forEach(({ vm }) => pauseVideoMedia(vm));
    };

    const isVideoPlaying = (video) => !video.paused && !video.ended;

    const cleanupEnded = () => {
      if (!endedHandler) return;
      playable.forEach(({ video }) => {
        video.removeEventListener("ended", endedHandler);
      });
      endedHandler = null;
    };

    const nextPlayToken = () => {
      playToken += 1;
      return playToken;
    };

    const playSequenceStep = (index) => {
      if (!isRunning || hoveredIndex !== null) return;

      const token = nextPlayToken();
      const nextIndex = index >= playable.length ? 0 : index;
      currentIndex = nextIndex;
      const item = playable[nextIndex];
      const { video } = item;

      cleanupEnded();
      pauseAll();
      setActive(nextIndex);
      video.loop = false;
      video.muted = true;

      const onEnded = () => {
        if (!isRunning || hoveredIndex !== null || token !== playToken) return;
        playSequenceStep(nextIndex + 1);
      };

      endedHandler = onEnded;

      const go = () => {
        if (token !== playToken || hoveredIndex !== null) return;
        playStepVideo(item, { fromStart: true });
        video.addEventListener("ended", onEnded);
      };

      if (video.readyState >= 2) {
        go();
      } else {
        video.addEventListener("canplay", go, { once: true });
      }
    };

    const beginHoverPreview = (index) => {
      if (leaveTimer) {
        clearTimeout(leaveTimer);
        leaveTimer = null;
      }

      if (
        hoveredIndex === null &&
        isRunning &&
        currentIndex === index &&
        isVideoPlaying(playable[index].video)
      ) {
        return;
      }

      nextPlayToken();
      hoveredIndex = index;
      currentIndex = index;
      cleanupEnded();
      pauseAll();
      setActive(index);

      const item = playable[index];
      item.video.loop = false;
      item.video.muted = true;
      playStepVideo(item, { fromStart: true });
    };

    const endHoverPreview = (index) => {
      if (hoveredIndex !== index) return;

      hoveredIndex = null;
      nextPlayToken();
      cleanupEnded();

      if (!isRunning) {
        pauseAll();
        clearActive();
        return;
      }

      playSequenceStep(currentIndex);
    };

    const scheduleLeave = (index, step) => {
      if (leaveTimer) clearTimeout(leaveTimer);
      leaveTimer = setTimeout(() => {
        leaveTimer = null;
        if (step.matches(":hover")) return;
        endHoverPreview(index);
      }, 100);
    };

    playable.forEach((item, index) => {
      const { step } = item;

      step.addEventListener(
        "pointerenter",
        () => beginHoverPreview(index),
        { signal }
      );

      step.addEventListener(
        "pointerleave",
        (e) => {
          if (step.contains(e.relatedTarget)) return;
          scheduleLeave(index, step);
        },
        { signal }
      );

      step.addEventListener(
        "focusin",
        () => beginHoverPreview(index),
        { signal }
      );

      step.addEventListener(
        "focusout",
        (e) => {
          if (!step.contains(e.relatedTarget)) {
            scheduleLeave(index, step);
          }
        },
        { signal }
      );
    });

    const start = () => {
      isRunning = true;
      hoveredIndex = null;
      playSequenceStep(0);
    };

    const stop = () => {
      isRunning = false;
      hoveredIndex = null;
      nextPlayToken();
      if (leaveTimer) clearTimeout(leaveTimer);
      cleanupEnded();
      pauseAll();
      clearActive();
    };

    return { start, stop };
  };

  const initTimeline = (section) => {
    const timeline = section.querySelector("[data-youp-timeline]");
    const steps = Array.from(section.querySelectorAll("[data-youp-timeline-step]"));
    if (!timeline || !steps.length) return;

    if (section._youpScanTimelineAbo) {
      section._youpScanTimelineAbo.abort();
    }
    section._youpScanTimelineAbo = new AbortController();
    const { signal } = section._youpScanTimelineAbo;

    const staggerMs = 200;
    const desktopTimeline = window.matchMedia("(min-width: 700px)").matches;
    const sequence = initTimelineSequence(section, steps, signal);

    steps.forEach((el, i) => {
      el.style.setProperty("--youp-timeline-step-delay", `${Math.min(staggerMs + i * staggerMs, 1200)}ms`);
    });

    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduceMotion || !("IntersectionObserver" in window)) {
      timeline.classList.add("is-animated");
      steps.forEach((el) => el.classList.add("is-inview"));
      sequence?.start();
      return;
    }

    let trackStarted = false;
    let viewportLeaveTimer = null;

    const revealAllSteps = () => {
      steps.forEach((el) => el.classList.add("is-inview"));
    };

    const startTrack = () => {
      timeline.classList.add("is-animated");
      if (desktopTimeline) {
        revealAllSteps();
      }
      if (!trackStarted) {
        trackStarted = true;
        sequence?.start();
      }
    };

    const rootIo = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (viewportLeaveTimer) {
              clearTimeout(viewportLeaveTimer);
              viewportLeaveTimer = null;
            }
            startTrack();
          } else {
            if (viewportLeaveTimer) clearTimeout(viewportLeaveTimer);
            viewportLeaveTimer = setTimeout(() => {
              viewportLeaveTimer = null;
              sequence?.stop();
              trackStarted = false;
            }, 400);
          }
        });
      },
      { root: null, rootMargin: "0px 0px -5% 0px", threshold: 0.05 }
    );
    rootIo.observe(timeline);

    signal.addEventListener("abort", () => {
      rootIo.disconnect();
      if (viewportLeaveTimer) clearTimeout(viewportLeaveTimer);
    });

    if (!desktopTimeline) {
      const stepIo = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add("is-inview");
              startTrack();
            }
          });
        },
        { root: null, rootMargin: "0px 0px -12% 0px", threshold: 0.18 }
      );

      steps.forEach((el) => stepIo.observe(el));
      signal.addEventListener("abort", () => stepIo.disconnect());
    }
  };

  const initSection = (section) => {
    section._youpScanTimelineAbo?.abort();
    initStepVideos(section);

    const layout = section.getAttribute("data-scan-layout") || "tabs";
    if (layout === "timeline") {
      initTimeline(section);
    } else {
      initTabs(section);
    }
  };

  const initAll = () => {
    document.querySelectorAll(sectionSelector).forEach(initSection);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAll);
  } else {
    initAll();
  }

  document.addEventListener("shopify:section:load", (event) => {
    if (event.target?.matches?.(sectionSelector)) {
      initSection(event.target);
    }
  });
})();
